import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

// 错误类型
export const UPLOAD_ERROR_TYPE = {
  // 无效文件类型
  INVALID_TYPE: "INVALID_TYPE",
  // 最小文件大小
  INVALID_MIN_SIZE: "INVALID_MIN_SIZE",
  // 最大文件大小
  INVALID_MAX_SIZE: "INVALID_MAX_SIZE",
  // 无效文件分辨率
  INVALID_RESOLUTION: "INVALID_RESOLUTION",
  // 图片加载错误
  IMAGE_LOAD_ERROR: "IMAGE_LOAD_ERROR",
} as const;

export type UploadErrorReason = keyof typeof UPLOAD_ERROR_TYPE;

export interface UseUploadOptions {
  acceptedFileTypes: string; // 自定义文件类型，如 ".jpg,.png,.gif" 或 "image/*,video/*"
  minFileSize?: number; // 最小文件大小（字节），可选
  maxFileSize?: number; // 最大文件大小（字节），可选
  multiple?: boolean; // 是否支持多文件上传
  disabled?: boolean; // 是否禁用上传，默认 false
  maxImageWidth?: number; // 最高宽度（像素）
  maxImageHeight?: number; // 最高高度（像素）
  onUpload: (file: File | File[]) => void;
  onInvalid: (reason?: UploadErrorReason) => void;
}

export interface UseUploadReturn {
  // ref 绑定方式
  uploadRef: React.RefObject<HTMLDivElement>;
  isDragOver: boolean;

  // 方法暴露方式
  triggerUpload: () => void;

  // 事件处理器（用于手动绑定）
  handlers: {
    onClick: () => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

export const useUpload = (options: UseUploadOptions): UseUploadReturn => {
  const {
    acceptedFileTypes,
    minFileSize = 0,
    maxFileSize = Infinity,
    maxImageWidth,
    maxImageHeight,
    multiple = false,
    disabled = false,
    onUpload,
    onInvalid,
  } = options;

  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const uploadRef = useRef<HTMLDivElement>(null);

  // 获取图片分辨率的辅助函数
  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) {
        reject("Not an image");
        return;
      }
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const dimensions = { width: img.width, height: img.height };
        URL.revokeObjectURL(img.src);
        resolve(dimensions);
      };
      img.onerror = () => reject("Failed to load image");
    });
  };

  // 核心验证逻辑
  const validateFile = useCallback(
    async (file: File): Promise<boolean> => {
      // 1. 验证文件类型
      const types = acceptedFileTypes.split(",").map((t) => t.trim().toLowerCase());
      const isValidType = types.some((type) => {
        if (type.startsWith(".")) {
          // 扩展名匹配
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        } else if (type.includes("*")) {
          // MIME 类型通配符匹配
          const pattern = type.replace("*", ".*");
          return new RegExp(pattern).test(file.type);
        } else {
          // 精确 MIME 类型匹配
          return file.type === type;
        }
      });

      if (!isValidType) {
        onInvalid(UPLOAD_ERROR_TYPE.INVALID_TYPE);
        return false;
      }

      // 2.校验文件大小下限
      if (minFileSize && file.size < minFileSize) {
        onInvalid(UPLOAD_ERROR_TYPE.INVALID_MIN_SIZE);
        return false;
      }

      // 2.校验文件大小上限
      if (maxFileSize && file.size > maxFileSize) {
        onInvalid(UPLOAD_ERROR_TYPE.INVALID_MAX_SIZE);
        return false;
      }

      // 3. 验证分辨率 (仅针对图片)
      if ((maxImageWidth || maxImageHeight) && file.type.startsWith("image/")) {
        try {
          const { width, height } = await getImageDimensions(file);
          if ((maxImageWidth && width > maxImageWidth) || (maxImageHeight && height > maxImageHeight)) {
            onInvalid(UPLOAD_ERROR_TYPE.INVALID_RESOLUTION);
            return false;
          }
        } catch (e) {
          onInvalid(UPLOAD_ERROR_TYPE.IMAGE_LOAD_ERROR);
          return false;
        }
      }

      return true;
    },
    [acceptedFileTypes, minFileSize, maxFileSize, maxImageWidth, maxImageHeight, onInvalid]
  );

  // 处理文件选择
  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files?.length || disabled) return;

      const results = await Promise.all(
        Array.from(files).map(async (file) => ({
          file,
          isValid: await validateFile(file),
        }))
      );

      const validFiles = results.filter((r) => r.isValid).map((r) => r.file);

      if (validFiles.length > 0) {
        if (multiple) {
          onUpload(validFiles);
        } else {
          onUpload(validFiles[0]);
        }
      }
    },
    [disabled, multiple, validateFile, onUpload]
  );

  // 触发上传（方法暴露方式）
  const triggerUpload = useCallback(() => {
    if (disabled) {
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptedFileTypes;
    input.multiple = multiple;
    input.style.display = "none";

    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      handleFileSelect(target.files);
      // 清理DOM元素
      document.body.removeChild(input);
    };

    // 添加到DOM并触发点击
    document.body.appendChild(input);
    input.click();
  }, [disabled, acceptedFileTypes, multiple, handleFileSelect]);

  // 点击上传处理器
  const handleClickUpload = useCallback(() => {
    triggerUpload();
  }, [triggerUpload]);

  // 拖拽事件处理器
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (disabled) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (disabled) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, [disabled]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (disabled) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  }, [disabled]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (disabled) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [disabled, handleFileSelect]);

  // 粘贴事件处理
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (disabled) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file && validateFile(file)) {
            onUpload(file);
          }
          break;
        }
      }
    };

    document.addEventListener("paste", handlePaste);

    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [disabled, validateFile, onUpload]);

  // 自动绑定事件到 ref 元素
  useEffect(() => {
    const element = uploadRef.current;
    if (!element) return;

    const onClick = () => handleClickUpload();
    const onDragEnter = (e: DragEvent) => handleDragEnter(e as any);
    const onDragLeave = (e: DragEvent) => handleDragLeave(e as any);
    const onDragOver = (e: DragEvent) => handleDragOver(e as any);
    const onDrop = (e: DragEvent) => handleDrop(e as any);

    element.addEventListener('click', onClick);
    element.addEventListener('dragenter', onDragEnter);
    element.addEventListener('dragleave', onDragLeave);
    element.addEventListener('dragover', onDragOver);
    element.addEventListener('drop', onDrop);

    return () => {
      element.removeEventListener('click', onClick);
      element.removeEventListener('dragenter', onDragEnter);
      element.removeEventListener('dragleave', onDragLeave);
      element.removeEventListener('dragover', onDragOver);
      element.removeEventListener('drop', onDrop);
    };
  }, [handleClickUpload, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return {
    uploadRef,
    isDragOver,
    triggerUpload,
    handlers: {
      onClick: handleClickUpload,
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
  };
};